/* useFilter.tsx */
import lodash from 'lodash';
import Category from '@shared/classes/category';
import EpgEvent from '@shared/classes/epgEvent';
import Rail from '@shared/classes/rail';
import { RailType } from '@shared/types/enums/rail';
import { setLayoutIndex } from '@shared/store/slices/main.slice';
import { store } from '@shared/store/store';
import { useAppDispatch } from '@shared/store/hooks';
import { useEffect, useRef } from 'react';
import { updateParams } from '@shared/store/slices/router.slice';

/**
 * Props for the useFilter hook.
 */
interface FilterProps {
  category: Category | undefined;
  channels: Rail | undefined;
  rails: Rail[] | undefined;
}

/**
 * Filter hook. Filters channels and rails based on the selected category.
 */
export default function useFilter({ category, channels, rails }: FilterProps) {
  // -------------------------------------------------------------
  // Misc Hooks & variables
  // -------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Direct store read (not `useAppSelector`) so this hook doesn't
  // re-subscribe on every router change.
  const currentParams = store.getState().router.params.current;

  // Clone channels and rails
  const initialCategory = useRef<Category | undefined>(category);
  const originalChannels = useRef<Rail>(
    lodash.cloneDeep(currentParams?.originalChannels as Rail)
  );
  const originalRails = useRef<Rail[]>(
    lodash.cloneDeep(currentParams?.originalRails as Rail[])
  );

  // Separate timers (not coalesced) — each writes a different key, so
  // losing one would lose that key's back-navigation restore data.
  const originalChannelsTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const originalRailsTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  // Tracks last-applied inputs so re-filtering (O(n) per EVENT/GUIDE_CHANNEL
  // rail) is skipped when category/channels/rails haven't changed — this
  // used to run every render and was slow enough that the initial page
  // stopped passing `filter` here at all.
  const lastAppliedRef = useRef<{
    category?: string;
    channels?: Rail;
    rails?: Rail[];
  } | null>(null);

  // -------------------------------------------------------------
  // Effect hooks
  // -------------------------------------------------------------

  // Update original channels
  useEffect(() => {
    // If original channels is not set, set it to the current channels
    if (!originalChannels.current?.data) {
      originalChannels.current = lodash.cloneDeep(channels);
    }
    // Update params (deferred past paint - pure back-navigation-restore
    // bookkeeping, see `writeParamsNow` in useMain.ts for the same reasoning)
    if (originalChannels.current?.data) {
      clearTimeout(originalChannelsTimeoutRef.current);
      originalChannelsTimeoutRef.current = setTimeout(() => {
        originalChannelsTimeoutRef.current = undefined;
        dispatch(
          updateParams({
            originalChannels: lodash.cloneDeep(originalChannels.current),
          })
        );
      }, 0);
    }
    return () => clearTimeout(originalChannelsTimeoutRef.current);
  }, [channels]);

  // Update original rails
  useEffect(() => {
    // If original rails is not set, set it to the current rails
    if (!originalRails.current?.length) {
      originalRails.current = lodash.cloneDeep(rails);
    }
    // If original rails is set, update it with the current rails
    else {
      rails?.forEach((rail) => {
        if (!originalRails.current.find((item: Rail) => item.id === rail.id)) {
          originalRails.current.push(lodash.cloneDeep(rail));
        }
      });
    }
    // Update params (deferred past paint, see above)
    if (originalRails.current?.length) {
      clearTimeout(originalRailsTimeoutRef.current);
      originalRailsTimeoutRef.current = setTimeout(() => {
        originalRailsTimeoutRef.current = undefined;
        dispatch(
          updateParams({
            originalRails: lodash.cloneDeep(originalRails.current),
          })
        );
      }, 0);
    }
    return () => clearTimeout(originalRailsTimeoutRef.current);
  }, [rails]);

  // Reset affected rails when category changes
  useEffect(() => {
    // If category is the same as the initial category, do nothing
    if (category?.id === initialCategory.current?.id) return;
    else initialCategory.current = undefined;

    // Reset affected rails
    dispatch(setLayoutIndex({ id: originalChannels.current?.id, index: 0 }));
    originalRails.current.forEach((rail: Rail) => {
      if (rail.type !== RailType.EVENT) return;
      dispatch(setLayoutIndex({ id: rail.id, index: 0 }));
    });
  }, [category]);

  // -------------------------------------------------------------
  // Filter logic
  // -------------------------------------------------------------

  const shouldReapply =
    lastAppliedRef.current?.category !== category?.id ||
    lastAppliedRef.current?.channels !== channels ||
    lastAppliedRef.current?.rails !== rails;

  if (shouldReapply) {
    lastAppliedRef.current = { category: category?.id, channels, rails };

    // Filter channels
    if (channels?.data && originalChannels.current?.data) {
      channels.data = (originalChannels.current.data as EpgEvent[])?.filter(
        (item) => {
          if (category?.id && item instanceof EpgEvent) {
            return item.channel?.category === category?.id;
          }
          return true;
        }
      );
    }

    // Filter rails
    if (rails?.length && originalRails.current.length) {
      rails.forEach((rail, index) => {
        if (
          rail.type !== RailType.EVENT &&
          rail.type !== RailType.GUIDE_CHANNEL
        )
          return;
        // Note: can't replace `rails[index]` with a new Rail instance here —
        // in production this array/its elements may be Immer-frozen (e.g.
        // once round-tripped through the router-params store), and while
        // mutating an existing instance's `.data` field is fine, writing a
        // new element into the array itself throws. `DefaultRail`/
        // `CircularRail` are memoized with a custom comparator that checks
        // `rail.data` identity precisely so this in-place mutation is enough
        // for them to notice and re-render.
        rail.data = originalRails.current[index]?.data?.filter(
          (item: unknown) => {
            if (category?.id && item instanceof EpgEvent) {
              return (
                category?.id === item.channel?.category ||
                category?.name === item.channel?.category
              );
            }
            return true;
          }
        );
      });
    }
  }
}
